import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const privacySections = [
    {
      title: 'Data Collection',
      content: [
        'Personal information including name, email, phone number, and wedding details when creating an account.',
        'Cultural preferences and ceremony requirements to match you with appropriate vendors.',
        'Location data to show relevant vendors in your area and provide location-based services.',
        'Communication records between couples and vendors to ensure service quality and resolve disputes.'
      ]
    },
    {
      title: 'Usage Purposes',
      content: [
        'Vendor matching based on your wedding preferences, budget, and cultural requirements.',
        'Platform improvement through analysis of user behavior and service effectiveness.',
        'Communication facilitation between couples and vendors through our messaging system.',
        'Cultural customization to provide relevant vendor recommendations and planning tools.'
      ]
    },
    {
      title: 'Information Sharing',
      content: [
        'Verified vendor partners receive limited contact information only when you express interest.',
        'Payment processing partners handle transaction data through secure, encrypted channels.',
        'Analytics providers receive anonymized usage data to help us improve platform performance.',
        'We never sell personal information to third parties or use it for unrelated marketing purposes.'
      ]
    },
    {
      title: 'Security Measures',
      content: [
        'Industry-standard encryption protects all data transmission and storage.',
        'Multi-factor authentication available for enhanced account security.',
        'Regular security audits and penetration testing to identify and address vulnerabilities.',
        'Immediate breach notification procedures in compliance with applicable data protection laws.'
      ]
    },
    {
      title: 'User Rights',
      content: [
        'Access your personal data and download a copy of your information at any time.',
        'Correct or update inaccurate information through your account settings.',
        'Delete your account and associated data with our secure data removal process.',
        'Data portability options to transfer your information to other platforms when technically feasible.'
      ]
    },
    {
      title: 'Cookie Policy',
      content: [
        'Essential cookies enable core platform functionality and secure user sessions.',
        'Analytics cookies help us understand how users interact with our platform.',
        'Preference cookies remember your settings and customization choices.',
        'You can manage cookie preferences through your browser settings or our privacy center.'
      ]
    }
  ];

  const dataTypes = [
    {
      category: 'Account Information',
      description: 'Basic profile details and authentication data',
      icon: 'User'
    },
    {
      category: 'Wedding Details',
      description: 'Event dates, locations, and cultural preferences',
      icon: 'Calendar'
    },
    {
      category: 'Communication',
      description: 'Messages between couples and vendors',
      icon: 'MessageCircle'
    },
    {
      category: 'Usage Analytics',
      description: 'Platform interaction and performance metrics',
      icon: 'BarChart3'
    }
  ];

  const userRights = [
    {
      right: 'Right to Access',
      description: 'Request copies of your personal data',
      action: 'Download Data'
    },
    {
      right: 'Right to Rectification',
      description: 'Correct inaccurate information',
      action: 'Update Profile'
    },
    {
      right: 'Right to Erasure',
      description: 'Delete your account and data',
      action: 'Delete Account'
    },
    {
      right: 'Right to Portability',
      description: 'Transfer data to another service',
      action: 'Export Data'
    }
  ];

  const handleContactUs = () => {
    navigate('/contact-us');
  };

  const handleTerms = () => {
    navigate('/terms-of-service');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy - ShaadiNest | Data Protection & User Privacy</title>
        <meta 
          name="description" 
          content="Learn how ShaadiNest protects your personal information, wedding details, and communication data through our comprehensive privacy policy and security measures." 
        />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                Privacy Policy
              </h1>
              <p className="text-xl text-text-secondary mb-4 max-w-3xl mx-auto">
                Your privacy is sacred to us. Learn how we protect your personal information and wedding details with the highest standards of data security.
              </p>
              <div className="flex items-center justify-center space-x-2 text-text-muted">
                <Icon name="Shield" size={16} />
                <span className="text-sm">Last updated: July 12, 2025</span>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Commitment */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-soft p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Shield" size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-xl text-text-primary mb-3">
                    Our Privacy Commitment
                  </h2>
                  <p className="text-text-secondary mb-4">
                    At ShaadiNest, we understand that wedding planning involves sharing deeply personal information about one of the most important days of your life. We are committed to protecting this information with the highest standards of privacy and security.
                  </p>
                  <p className="text-text-secondary">
                    This Privacy Policy explains how we collect, use, share, and protect your information when you use our South Asian wedding marketplace platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data We Collect */}
        <section className="py-12 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Information We Collect
              </h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                We collect information to provide you with the best possible wedding planning experience
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {dataTypes.map((type, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={type.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {type.category}
                  </h3>
                  <p className="text-text-secondary text-sm">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Privacy Sections */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {privacySections.map((section, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-8">
                  <h3 className="font-heading font-semibold text-xl text-text-primary mb-6 flex items-center">
                    <span className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">
                      {index + 1}
                    </span>
                    {section.title}
                  </h3>
                  <div className="space-y-4">
                    {section.content.map((paragraph, paragraphIndex) => (
                      <div key={paragraphIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-text-secondary leading-relaxed">{paragraph}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Rights */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Your Privacy Rights
              </h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                You have full control over your personal information and how it's used
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {userRights.map((right, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6">
                  <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center mb-4">
                    <Icon name="Check" size={20} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {right.right}
                  </h3>
                  <p className="text-text-secondary text-sm mb-4">{right.description}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    onClick={handleContactUs}
                  >
                    {right.action}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Security Measures */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-heading font-bold text-3xl text-text-primary mb-6">
                    Bank-Level Security
                  </h2>
                  <p className="text-lg text-text-secondary mb-6">
                    Your data is protected by the same security standards used by financial institutions. We employ multiple layers of protection to keep your information safe.
                  </p>
                  <div className="space-y-4">
                    {[
                      'End-to-end encryption for all data transmission',
                      '24/7 monitoring and threat detection',
                      'Regular security audits and compliance checks',
                      'Secure data centers with physical protection'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Icon name="CheckCircle" size={20} className="text-success" />
                        <span className="text-text-secondary">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="w-32 h-32 bg-white rounded-full shadow-strong flex items-center justify-center mx-auto mb-6">
                    <Icon name="Lock" size={64} className="text-primary" />
                  </div>
                  <p className="text-text-secondary text-sm">
                    SSL Encrypted • GDPR Compliant • SOC 2 Certified
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-soft p-8">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
                Privacy Questions or Concerns?
              </h3>
              <p className="text-text-secondary mb-6">
                Our privacy team is here to help. If you have any questions about this policy or how we handle your data, please don't hesitate to reach out.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <Icon name="Mail" size={32} className="text-primary mx-auto mb-2" />
                  <p className="font-medium text-text-primary">Email</p>
                  <p className="text-sm text-text-secondary">privacy@shaadinest.com</p>
                </div>
                <div className="text-center">
                  <Icon name="Phone" size={32} className="text-primary mx-auto mb-2" />
                  <p className="font-medium text-text-primary">Phone</p>
                  <p className="text-sm text-text-secondary">(555) 123-4567</p>
                </div>
                <div className="text-center">
                  <Icon name="Clock" size={32} className="text-primary mx-auto mb-2" />
                  <p className="font-medium text-text-primary">Response Time</p>
                  <p className="text-sm text-text-secondary">Within 24 hours</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="primary"
                  size="md"
                  iconName="Mail"
                  onClick={handleContactUs}
                >
                  Contact Privacy Team
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  iconName="FileText"
                  onClick={handleTerms}
                >
                  View Terms of Service
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Version History */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-surface-100 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center">
                <Icon name="History" size={20} className="mr-2" />
                Policy Updates
              </h3>
              <p className="text-text-secondary text-sm mb-3">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
                When we make significant changes, we'll notify you via email and through prominent notices on our platform.
              </p>
              <p className="text-text-secondary text-sm">
                Current version: 2.1 • Last updated: July 12, 2025 • Effective date: July 12, 2025
              </p>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;